import type { ServiceProcedureConsumable } from '../types/companyResourceTypes';
import type { ExpenseCategoryEntry, PmPartPriceSnapshot } from '../types/costTrackingTypes';
import type { PMPartsAndProcedure, PMServiceLevel } from '../types/pmTypes';
import { isProcedureType, linkedConsumablesForProcedure } from './companyResourceUtils';
import { costCategoryFromItem, sumPmPartPriceSnapshots } from './costTrackingUtils';
import {
    getAssignmentQuantity,
    getEffectiveMatrixQuantityForPart,
    parseServiceLevelAssignments,
} from './pmFormUtils';

export const PM_SERVICE_WORK_DESCRIPTION_PREFIX = 'PM Service';
export const PM_INTERNAL_LABOR_CATEGORY = 'Internal Labor';
/** One total for the completed PM level. Item lines stay off the cost record. */
export const PM_INTERNAL_SERVICE_CATEGORY = 'Internal PM Service';

export interface PmProcedureCostContext {
    procedures: Array<{ id: string; itemName: string; category?: string | null }>;
    links: ServiceProcedureConsumable[];
    catalog: PMPartsAndProcedure[];
}

export interface PmLaborEntryInput {
    categoryId: string;
    categoryName: string;
    standardRate: number;
    overtimeRate: number;
    hoursST: number;
    hoursOT: number;
}

export interface PmLaborLineSnapshot {
    categoryId: string;
    categoryName: string;
    hoursST: number;
    hoursOT: number;
    rateST: number;
    rateOT: number;
    cost: number;
}

export interface PmLaborSummary {
    laborHoursST: number;
    laborHoursOT: number;
    /** Blended so summed hours × these rates equal totalLaborCost across categories. */
    laborRateST: number;
    laborRateOT: number;
    totalLaborCost: number;
    laborRateScheduleId: string;
    laborRateScheduleName: string;
    lines: PmLaborLineSnapshot[];
}

/**
 * Cost Tracking posts when the company module is on.
 * The Cost tab uses that same company flag. Unit enrollment is not set by
 * PM enrollment, so an unset flag still posts. An explicit false opts the unit out.
 */
export function isPmCostTrackingEnabled(
    company?: { costTrackingModuleEnrollment?: boolean | null } | null,
    unit?: { costTrackingModuleEnrollment?: boolean | null } | null,
): boolean {
    if (!company?.costTrackingModuleEnrollment) return false;
    return unit?.costTrackingModuleEnrollment !== false;
}

/**
 * Equipment Category on a Cost Tracking expense. Prefer the unit’s categoryId;
 * fall back to the nested category relation when the list omitted the field.
 */
export function resolveEquipmentCategoryIdForUnit(
    unit?: { categoryId?: string | null; category?: { id?: string | null } | null } | null,
): string | null {
    const fromId = unit?.categoryId?.trim();
    if (fromId) return fromId;
    const fromRelation = unit?.category?.id?.trim();
    return fromRelation || null;
}

/**
 * Matrix quantity for a part across due levels. Cascade unions tasks — take the
 * largest assigned qty so the same oil filter is not multiplied across levels.
 * When `allLevels` is passed, nested lower-level assignments (Service Matrix
 * inheritance) are included the same way as inspect Required Parts.
 */
export function getMatrixQuantityForPart(
    partId: string,
    serviceLevels: PMServiceLevel[],
    allLevels?: PMServiceLevel[],
): number {
    if (allLevels && allLevels.length > 0) {
        return getEffectiveMatrixQuantityForPart(partId, serviceLevels, allLevels);
    }
    let maxQty = 0;
    for (const level of serviceLevels) {
        const assignments = parseServiceLevelAssignments(level.serviceLevelAssignments);
        maxQty = Math.max(maxQty, getAssignmentQuantity(assignments[partId]));
    }
    return maxQty;
}

/**
 * Over/Under Target % = Actual UM ÷ Target UM (as a percentage).
 * Returns null when either value is missing or non-positive.
 */
export function calculateEfficiencyFactor(
    actualUM: number,
    targetUM: number,
): number | null {
    if (!(actualUM > 0) || !(targetUM > 0)) return null;
    return (actualUM / targetUM) * 100;
}

/**
 * Price snapshots for Cost Tracking: only **checked** consumables.
 * Unchecked rows are omitted so dollars do not post (Concept).
 * Quantity comes from the service matrix, not catalog defaults.
 */
function normalizeName(value?: string | null): string {
    return (value || '').trim().toLowerCase();
}

/**
 * Matrix procedures are catalog copies matched to Service Procedures by name
 * and category. Ambiguous name-only matches are skipped.
 */
export function matchServiceProcedureForMatrixPart(
    part: { itemName?: string | null; category?: string | null; itemType?: string | null },
    procedures: Array<{ id: string; itemName: string; category?: string | null }>,
): { id: string } | null {
    if (!isProcedureType(part.itemType)) return null;
    const name = normalizeName(part.itemName);
    if (!name) return null;
    const byName = procedures.filter((procedure) => normalizeName(procedure.itemName) === name);
    const category = normalizeName(part.category);
    if (category) {
        const byBoth = byName.filter((procedure) => normalizeName(procedure.category) === category);
        return byBoth[0] ?? null;
    }
    return byName.length === 1 ? byName[0] : null;
}

/**
 * Flat-rate consumables on completed procedures. Charged once per completed
 * procedure row, not once per service level in the cascade.
 */
export function buildPmProcedureConsumableSnapshots(
    parts: PMPartsAndProcedure[],
    completedItems: Record<string, boolean>,
    procedureCosts: PmProcedureCostContext,
): PmPartPriceSnapshot[] {
    const snapshots: PmPartPriceSnapshot[] = [];

    for (const part of parts) {
        if (!part?.id || !completedItems[part.id]) continue;
        const procedure = matchServiceProcedureForMatrixPart(part, procedureCosts.procedures);
        if (!procedure) continue;

        for (const { link, item } of linkedConsumablesForProcedure(
            procedureCosts.links,
            procedureCosts.catalog,
            procedure.id,
        )) {
            const quantity = Number(link.quantity) || 0;
            const unitPrice = Number(item?.price) || 0;
            const lineTotal = quantity * unitPrice;
            if (!item?.id || lineTotal <= 0) continue;
            snapshots.push({
                serviceItemId: item.id,
                itemName: item.itemName,
                quantity,
                unitPrice,
                lineTotal,
                ...costCategoryFromItem(item),
            });
        }
    }

    return snapshots;
}

export function buildPmInspectCostSnapshots(
    parts: PMPartsAndProcedure[],
    serviceLevels: PMServiceLevel[],
    completedItems: Record<string, boolean>,
    procedureCosts?: PmProcedureCostContext | null,
    allLevels?: PMServiceLevel[],
): PmPartPriceSnapshot[] {
    const snapshots: PmPartPriceSnapshot[] = [];

    for (const part of parts) {
        if (!part?.id || !completedItems[part.id]) continue;
        if (isProcedureType(part.itemType)) continue;

        const quantity = getMatrixQuantityForPart(part.id, serviceLevels, allLevels);
        if (quantity <= 0) continue;

        const unitPrice = Number(part.price) || 0;
        const lineTotal = quantity * unitPrice;
        if (lineTotal <= 0) continue;

        snapshots.push({
            serviceItemId: part.id,
            itemName: part.itemName,
            quantity,
            unitPrice,
            lineTotal,
            ...costCategoryFromItem(part),
        });
    }

    if (procedureCosts) {
        snapshots.push(...buildPmProcedureConsumableSnapshots(parts, completedItems, procedureCosts));
    }

    return snapshots;
}

export function summarizePmLaborEntries(entries: PmLaborEntryInput[]): PmLaborSummary {
    const lines: PmLaborLineSnapshot[] = [];
    let laborHoursST = 0;
    let laborHoursOT = 0;
    let straightCost = 0;
    let overtimeCost = 0;

    for (const entry of entries) {
        if (!entry.categoryId) continue;
        const hoursST = Number(entry.hoursST) || 0;
        const hoursOT = Number(entry.hoursOT) || 0;
        if (hoursST <= 0 && hoursOT <= 0) continue;
        const rateST = Number(entry.standardRate) || 0;
        const rateOT = Number(entry.overtimeRate) || 0;
        const cost = hoursST * rateST + hoursOT * rateOT;
        laborHoursST += hoursST;
        laborHoursOT += hoursOT;
        straightCost += hoursST * rateST;
        overtimeCost += hoursOT * rateOT;
        lines.push({
            categoryId: entry.categoryId,
            categoryName: entry.categoryName,
            hoursST,
            hoursOT,
            rateST,
            rateOT,
            cost,
        });
    }

    const names = [...new Set(lines.map((line) => line.categoryName).filter(Boolean))];
    return {
        laborHoursST,
        laborHoursOT,
        laborRateST: laborHoursST > 0 ? straightCost / laborHoursST : 0,
        laborRateOT: laborHoursOT > 0 ? overtimeCost / laborHoursOT : 0,
        totalLaborCost: straightCost + overtimeCost,
        laborRateScheduleId: lines.length === 1 ? lines[0].categoryId : '',
        laborRateScheduleName: names.join(', '),
        lines,
    };
}

export function parsePmLaborLines(reportData?: string | null): PmLaborLineSnapshot[] {
    if (!reportData) return [];
    try {
        const parsed = JSON.parse(reportData) as { laborLines?: PmLaborLineSnapshot[] };
        if (!Array.isArray(parsed?.laborLines)) return [];
        return parsed.laborLines.filter((line) => line && (line.hoursST > 0 || line.hoursOT > 0));
    } catch {
        return [];
    }
}

/** People see the service. The report id stays on the invoice field for matching, not in this text. */
export function pmServiceExpenseWorkDescription(serviceLevelName?: string | null): string {
    const level = serviceLevelName?.trim();
    return level
        ? `${PM_SERVICE_WORK_DESCRIPTION_PREFIX} · ${level}`
        : PM_SERVICE_WORK_DESCRIPTION_PREFIX;
}

/**
 * A completed PM already chose the service level. Saving the cost record for an
 * enrolled unit must keep that level. A hand-entered expense uses the dropdown,
 * and keeps a stored multi-level name when the selection has not changed.
 */
export function pmServiceLevelFieldsToSave(args: {
    postsOnSubmit: boolean;
    selectedLevelId?: string | null;
    selectedLevelName?: string | null;
    storedLevelId?: string | null;
    storedLevelName?: string | null;
}): { pmServiceLevelId: string | null; pmServiceLevelName: string | null } {
    const storedId = args.storedLevelId?.trim() || '';
    const storedName = args.storedLevelName?.trim() || '';
    if (args.postsOnSubmit) {
        return {
            pmServiceLevelId: storedId || null,
            pmServiceLevelName: storedName || null,
        };
    }
    const selectedId = args.selectedLevelId?.trim() || '';
    if (storedId && selectedId === storedId && storedName) {
        return { pmServiceLevelId: selectedId, pmServiceLevelName: storedName };
    }
    const selectedName = args.selectedLevelName?.trim() || '';
    return {
        pmServiceLevelId: selectedId || null,
        pmServiceLevelName: selectedName || null,
    };
}

export function isPmServiceExpenseForReport(
    expense: { invoiceNumber?: string | null; workDescription?: string | null },
    reportId: string,
): boolean {
    if (!reportId) return false;
    if (expense.invoiceNumber === reportId) return true;
    return Boolean(expense.workDescription?.includes(reportId));
}

export function buildPmServiceExpenseInput(args: {
    companyId: string;
    unitId: string;
    equipmentCategoryId?: string | null;
    reportId: string;
    dateOfService: string;
    serviceLevelId?: string | null;
    serviceLevelName?: string | null;
    snapshots: PmPartPriceSnapshot[];
    laborHoursST: number;
    laborHoursOT: number;
    laborRateST: number;
    laborRateOT: number;
    laborRateScheduleId?: string | null;
    laborRateScheduleName?: string | null;
}): Record<string, unknown> | null {
    if (!args.companyId || !args.unitId || !args.reportId) return null;

    const partsTotal = sumPmPartPriceSnapshots(args.snapshots);
    const laborCost =
        args.laborHoursST * args.laborRateST + args.laborHoursOT * args.laborRateOT;
    const categoryEntries: ExpenseCategoryEntry[] = [];
    if (partsTotal > 0) {
        categoryEntries.push({
            categoryId: '',
            categoryName: PM_INTERNAL_SERVICE_CATEGORY,
            cost: partsTotal,
        });
    }
    if (laborCost > 0) {
        categoryEntries.push({ categoryId: '', categoryName: PM_INTERNAL_LABOR_CATEGORY, cost: laborCost });
    }
    const categoryCost = categoryEntries.reduce((sum, entry) => sum + entry.cost, 0);
    const totalExpense = categoryCost;

    return {
        companyId: args.companyId,
        unitId: args.unitId,
        equipmentCategoryId: args.equipmentCategoryId || null,
        dateOfInput: args.dateOfService,
        dateOfService: args.dateOfService,
        workDescription: pmServiceExpenseWorkDescription(args.serviceLevelName),
        invoiceNumber: args.reportId,
        pmServiceLevelId: args.serviceLevelId || null,
        pmServiceLevelName: args.serviceLevelName || null,
        pmExpense: 0,
        pmPartsSnapshot: '[]',
        expenseCategories: JSON.stringify(categoryEntries),
        laborEntries: JSON.stringify(
            laborCost > 0 || args.laborHoursST > 0 || args.laborHoursOT > 0
                ? [{
                    categoryId: args.laborRateScheduleId || '',
                    categoryName: args.laborRateScheduleName || PM_INTERNAL_LABOR_CATEGORY,
                    hoursST: args.laborHoursST,
                    hoursOT: args.laborHoursOT,
                    rateST: args.laborRateST,
                    rateOT: args.laborRateOT,
                    cost: laborCost,
                }]
                : [],
        ),
        laborRateScheduleId: args.laborRateScheduleId || null,
        laborRateScheduleName: args.laborRateScheduleName || null,
        laborHoursST: args.laborHoursST,
        laborHoursOT: args.laborHoursOT,
        laborRateST: args.laborRateST,
        laborRateOT: args.laborRateOT,
        totalLaborCost: laborCost,
        totalExpense,
    };
}
